// src/pages/tasks/TaskDetailModal.jsx
import { useState, useEffect } from "react";
import * as api from "../../apiService";
import { fmtDateTime } from "../../utils/dateUtils";

const STATUSES = ["BACKLOG", "TO_DO", "IN_PROGRESS", "REVIEW", "DONE"];

const STATUS_CFG = {
  BACKLOG:     { label: "Backlog",     bg: "#f1f5f9", color: "#475569" },
  TO_DO:       { label: "To Do",       bg: "#dbeafe", color: "#1d4ed8" },
  IN_PROGRESS: { label: "In Progress", bg: "#fef3c7", color: "#92400e" },
  REVIEW:      { label: "Review",      bg: "#ede9fe", color: "#5b21b6" },
  DONE:        { label: "Done",        bg: "#d1fae5", color: "#065f46" },
};

const PRIORITY_CFG = {
  LOW:    { label: "Low",    bg: "#f1f5f9", color: "#475569" },
  MEDIUM: { label: "Medium", bg: "#dbeafe", color: "#1d4ed8" },
  HIGH:   { label: "High",   bg: "#fef3c7", color: "#92400e" },
  URGENT: { label: "Urgent", bg: "#fee2e2", color: "#991b1b" },
};

function Badge({ cfg }) {
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.color}33`,
      borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600,
    }}>
      {cfg.label}
    </span>
  );
}

export function TaskDetailModal({ task, userEmail, canManage, onStatusChange, onClose }) {
  const [comments,    setComments]    = useState([]);
  const [commentText, setCommentText] = useState("");
  const [loadingC,    setLoadingC]    = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState(null);
  const [newStatus,   setNewStatus]   = useState(task.status);

  const isAssignee = userEmail === task.assignedTo;
  const canAct     = canManage || isAssignee;

  useEffect(() => {
    api.fetchComments(task.id)
      .then(setComments)
      .catch(e => setError(e.message))
      .finally(() => setLoadingC(false));
  }, [task.id]);

  async function handleStatusSave() {
    if (newStatus === task.status) return;
    setSaving(true); setError(null);
    try {
      const updated = await api.updateTaskStatus(task.id, newStatus);
      onStatusChange(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleAddComment(e) {
    e.preventDefault();
    if (!commentText.trim()) return;
    setSaving(true); setError(null);
    try {
      const c = await api.addComment(task.id, { body: commentText.trim() });
      setComments(prev => [...prev, c]);
      setCommentText("");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const sc = STATUS_CFG[task.status]   ?? STATUS_CFG.BACKLOG;
  const pc = PRIORITY_CFG[task.priority] ?? PRIORITY_CFG.MEDIUM;

  return (
    <div className="modal wide" style={{ maxWidth: 600 }}>
      <div className="modal-header">
        <h3 className="modal-title" style={{ flex: 1, paddingRight: 12 }}>{task.title}</h3>
        <button className="btn" onClick={onClose}>✕</button>
      </div>

      {/* Meta row */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <Badge cfg={sc} />
        <Badge cfg={pc} />
        {task.department && (
          <span style={{ fontSize: 11, background: "#f1f5f9", color: "#374151",
            border: "1px solid #e2e8f0", borderRadius: 4, padding: "2px 8px", fontWeight: 600 }}>
            {task.department}
          </span>
        )}
      </div>

      <div style={{ fontSize: 13, color: "#374151", marginBottom: 12 }}>
        <span style={{ color: "#6b7280" }}>Assigned to: </span>
        <strong>{task.assignedTo}</strong>
        {task.dueDate && (
          <span style={{ marginLeft: 16, color: "#6b7280" }}>
            Due: <strong style={{ color: isDue(task.dueDate) ? "#dc2626" : "#374151" }}>{task.dueDate}</strong>
          </span>
        )}
      </div>

      {task.description && (
        <div style={{ fontSize: 13, color: "#374151", background: "#f8fafc",
          border: "1px solid #e2e8f0", borderRadius: 6, padding: "10px 12px", marginBottom: 16,
          whiteSpace: "pre-wrap" }}>
          {task.description}
        </div>
      )}

      {/* Status update (assignee or manager) */}
      {canAct && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <span style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>Update status:</span>
          <select className="form-select" value={newStatus}
            onChange={e => setNewStatus(e.target.value)} style={{ width: 160 }}>
            {STATUSES.map(s => (
              <option key={s} value={s}>{STATUS_CFG[s]?.label ?? s}</option>
            ))}
          </select>
          <button className="btn btn-primary" onClick={handleStatusSave}
            disabled={saving || newStatus === task.status} style={{ fontSize: 12, padding: "4px 12px" }}>
            Save
          </button>
        </div>
      )}

      <div className="form-sep" />

      {/* Comments */}
      <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
        Comments
      </div>

      {loadingC && <div style={{ color: "#aaa", fontSize: 12 }}>Loading comments…</div>}

      <div style={{ maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
        {comments.length === 0 && !loadingC && (
          <div style={{ color: "#aaa", fontSize: 12 }}>No comments yet.</div>
        )}
        {comments.map(c => (
          <div key={c.id} style={{ background: "#f8fafc", border: "1px solid #e2e8f0",
            borderRadius: 6, padding: "8px 10px" }}>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>
              <strong style={{ color: "#374151" }}>{c.author}</strong>
              {" · "}{fmtDateTime(c.createdAt)}
            </div>
            <div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{c.body}</div>
          </div>
        ))}
      </div>

      {canAct && (
        <form onSubmit={handleAddComment} style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <textarea className="form-input" rows={2} placeholder="Add a comment…"
            value={commentText} onChange={e => setCommentText(e.target.value)}
            style={{ flex: 1, resize: "vertical" }} />
          <button type="submit" className="btn btn-primary" disabled={saving || !commentText.trim()}
            style={{ padding: "6px 14px" }}>
            Post
          </button>
        </form>
      )}

      {error && <div className="alert-warn" style={{ marginTop: 8 }}>{error}</div>}
    </div>
  );
}

function isDue(dateStr) {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
}
